<template>
    <AppModal ref="modal" :title="$t('imageGenerationModal.modalTitle')" size="lg" @on-hide="hide">
        <WizardForm
            :steps="steps"
            @submit="hide"
        />
    </AppModal>
</template>
<script>
import AppModal from '../../../../../../../../components/new/modal/AppModal.vue';
import WizardForm from '../../../../../../../../components/new/form/wizardForm/WizardForm.vue';

import { httpClient } from '../../../../../../../../services/HttpClient';
import { generateUrl } from '../../../../../../../../utils';
import { defineAsyncComponent } from 'vue';

const SelectReferenceStep = defineAsyncComponent(() => import('./wizardSteps/SelectReferenceStep.vue'));
const ModificationSettingsStep = defineAsyncComponent(() => import('./wizardSteps/ModificationSettingsStep.vue'));
const GroupProductGenerationStep = defineAsyncComponent(() => import('./wizardSteps/GroupProductGenerationStep.vue'));

export default {
    name: 'ImageGenerationFromGroupProductModal',
    components: {
        AppModal, WizardForm
    },
    emits: ['saveGeneratedImage'],
    data() {
        return {
            product: {
                name: null,
                description: null,
                images: null
            },
            groupProducts: [],
            selectedReference: null,
            selectedFormatKey: '1:1',
            selectedResolutionKey: '1K',
            selectedEditablePhotoProperties: [],
            selectedModificationReference: null,
            tokenBalance: null,
        }
    },
    methods: {
        async show(product) {
            this.product = {
                uuid: product.uuid,
                name: product.names[Object.keys(product.names)[0]],
                description: product.descriptions[Object.keys(product.descriptions)[0]],
                images: product.images,
                groupKey: product.group_key
            }

            await this.fetchBalance();
            await this.fetchGroupProducts();

            this.$refs.modal.show();
        },
        hide() {
            this.$refs.modal.hide();
            this.product = {
                name: null,
                description: null,
                images: null
            };
            this.groupProducts = [];
            this.selectedReference = null;
            this.selectedFormatKey = '1:1';
            this.selectedResolutionKey = '1K';
            this.selectedEditablePhotoProperties = [];
            this.selectedModificationReference = null;
            this.tokenBalance = null;
        },
        async fetchGroupProducts() {
            const limit = 100;
            const products = [];

            const firstResponse = await httpClient.get(`/integrations/${this.$route.params.integrationUuid}/products`, {
                group_keys: [this.product.groupKey],
                page: 1,
                limit
            });
            products.push(...firstResponse.data.data);

            const totalPages = Math.ceil(firstResponse.data.meta.total / limit);
            for (let page = 2; page <= totalPages; page++) {
                const response = await httpClient.get(`/integrations/${this.$route.params.integrationUuid}/products`, {
                    group_keys: [this.product.groupKey],
                    page,
                    limit
                });
                products.push(...response.data.data);
            }

            this.groupProducts = products
                .filter(product => product.uuid !== this.product.uuid && product.images.length > 0 && product.sku !== null)
                .map(product => ({
                    uuid: product.uuid,
                    sku: product.sku,
                    images: product.images
                }));
        },
        base64ToFile(base64String, filename) {
            const base64Data = base64String.replace(/^data:image\/[a-z+]+;base64,/, '');
            const byteCharacters = atob(base64Data);
            const byteNumbers = new Array(byteCharacters.length);

            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }

            const byteArray = new Uint8Array(byteNumbers);
            return new File([byteArray], filename, { type: 'image/png' });
        },
        getImageDataUrlFromBase64(base64String) {
            if (base64String.startsWith('data:image')) {
                return base64String;
            }
            return `data:image/png;base64,${base64String}`;
        },
        saveGeneratedImage(files) {
            this.$emit('saveGeneratedImage', files);
        },
        async urlToBase64(imageUrl) {
            const response = await httpClient.post(generateUrl('proxy/http-request'), {
                method: 'GET',
                url: imageUrl
            }, true, {
                responseType: 'arraybuffer',
            });

            if (response.status !== 200) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const bytes = new Uint8Array(response.data);
            const binaryString = bytes.reduce((data, byte) => data + String.fromCharCode(byte), '');
            return btoa(binaryString);
        },
        async fetchBalance() {
            const response = await httpClient.get(`/projects/${this.$route.params.projectUuid}/core/ai-transactions/balance`);
            this.tokenBalance = response.data.balance;
        },
    },
    computed: {
        steps() {
            return [
                {
                    name: this.$t('imageGenerationModal.steps.selectReference'),
                    component: SelectReferenceStep,
                    props: {
                        groupProducts: this.groupProducts,
                        selectedReference: this.selectedReference,
                        'onUpdate:selectedReference': (value) => this.selectedReference = value
                    }
                },
                {
                    name: this.$t('imageGenerationModal.steps.settings'),
                    component: ModificationSettingsStep,
                    props: {
                        selectedFormatKey: this.selectedFormatKey,
                        selectedResolutionKey: this.selectedResolutionKey,
                        productImages: this.product.images,
                        selectedEditablePhotoProperties: this.selectedEditablePhotoProperties,
                        selectedModificationReference: this.selectedModificationReference,
                        getImageDataUrlFromBase64: this.getImageDataUrlFromBase64,
                        base64ToFile: this.base64ToFile,
                        urlToBase64: this.urlToBase64,
                        'onUpdate:selectedFormatKey': (value) => this.selectedFormatKey = value,
                        'onUpdate:selectedResolutionKey': (value) => this.selectedResolutionKey = value,
                        'onUpdate:selectedEditablePhotoProperties': (value) => this.selectedEditablePhotoProperties = value,
                        'onUpdate:selectedModificationReference': (value) => this.selectedModificationReference = value
                    }
                },
                {
                    name: this.$t('imageGenerationModal.steps.generation'),
                    component: GroupProductGenerationStep,
                    props: {
                        referenceImageUrl: this.selectedReference,
                        selectedFormatKey: this.selectedFormatKey,
                        selectedResolutionKey: this.selectedResolutionKey,
                        product: this.product,
                        selectedEditablePhotoProperties: this.selectedEditablePhotoProperties,
                        selectedModificationReference: this.selectedModificationReference,
                        base64ToFile: this.base64ToFile,
                        getImageDataUrlFromBase64: this.getImageDataUrlFromBase64,
                        urlToBase64: this.urlToBase64,
                        onSaveGeneratedImage: this.saveGeneratedImage,
                        onTokensFlushed: this.fetchBalance,
                        tokenBalance: this.tokenBalance,
                    }
                }
            ]
        }
    }
}
</script>